import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, AlertTriangle, CheckCircle2, XCircle, RefreshCw, Lock,
  BarChart2, FileText, Clock, GitBranch, Activity, TrendingUp,
  AlertCircle, ChevronDown, ChevronUp
} from "lucide-react";
import client from "@/api/client";

// ── Integrity score ring ──────────────────────────────────────────────────────
function ScoreRing({ score, size = 56 }) {
  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  const color = score >= 80 ? "#34d399" : score >= 60 ? "#f59e0b" : "#ef4444";
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#1e293b" strokeWidth="5"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${filled} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}/>
      <text x={size/2} y={size/2+4} textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>{score}</text>
    </svg>
  );
}

// ── Summary stat card ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = "text-white", bg = "bg-slate-800", icon: Icon }) {
  return (
    <div className={`${bg} border border-slate-700 rounded-xl p-4 flex items-start gap-3`}>
      {Icon && <div className="mt-0.5 p-2 bg-slate-700/50 rounded-lg flex-shrink-0"><Icon size={14} className={color} /></div>}
      <div className="min-w-0">
        <p className="text-3xl font-black text-white leading-none">{value}</p>
        <p className="text-xs text-slate-400 mt-1">{label}</p>
        {sub && <p className={`text-xs font-semibold mt-0.5 ${color}`}>{sub}</p>}
      </div>
    </div>
  );
}

// ── Project row ───────────────────────────────────────────────────────────────
function ProjectRow({ project }) {
  const [expanded, setExpanded] = useState(false);
  const ready = project.report_ready;
  const score = project.integrity_score;
  const hasFlags = project.governance_flags?.length > 0;

  return (
    <div className={`border-b border-slate-800 last:border-0 ${hasFlags ? "bg-red-950/10" : ""}`}
      data-testid={`integrity-row-${project.id}`}>
      <div className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-slate-800/40 transition-colors"
        onClick={() => setExpanded(v => !v)}>
        {/* Score ring */}
        <ScoreRing score={score} size={48} />

        {/* Project info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">{project.name || `Project ${project.id}`}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">{project.sector || "—"}</span>
            {project.run_label && (
              <span className="text-[10px] text-slate-600 truncate">· {project.run_label}</span>
            )}
          </div>
        </div>

        {/* Report ready badge */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {ready ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-900/60 border border-emerald-700 text-emerald-300">
              <CheckCircle2 size={8} /> Report Ready
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-900/40 border border-red-700/50 text-red-400">
              <XCircle size={8} /> Not Ready
            </span>
          )}

          {project.approved_baseline?.run_id && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-900/40 border border-blue-700/50 text-blue-300">
              <Lock size={8} /> Baseline
            </span>
          )}

          {hasFlags && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-900/40 border border-amber-700/50 text-amber-400">
              <AlertTriangle size={8} /> {project.governance_flags.length} Flag{project.governance_flags.length > 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Run counts */}
        <div className="flex items-center gap-3 text-xs text-slate-500 flex-shrink-0 w-32 text-right">
          <span>{project.approved_count} approved</span>
          <span>{project.draft_count} drafts</span>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 pb-4 pt-0 bg-slate-900/50">
          <div className="grid grid-cols-4 gap-3 mt-2 mb-3">
            {[
              ["Validation (40%)", project.score_breakdown?.validation_40, "text-teal-400"],
              ["Governance (30%)", project.score_breakdown?.governance_30, "text-blue-400"],
              ["Traceability (20%)", project.score_breakdown?.traceability_20, "text-purple-400"],
              ["Evidence (10%)", project.score_breakdown?.evidence_10, "text-amber-400"],
            ].map(([label, val, col]) => (
              <div key={label} className="bg-slate-800 rounded-lg px-3 py-2">
                <p className={`text-base font-black ${col}`}>{val}/{label.match(/(\d+)%/)?.[1]}</p>
                <p className="text-[9px] text-slate-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {project.approved_baseline?.value_tco2e && (
            <div className="flex items-center gap-2 mb-2">
              <Lock size={10} className="text-emerald-400" />
              <span className="text-xs text-emerald-300 font-semibold">
                Approved Baseline (Run {project.approved_baseline.run_id}):
                &nbsp;{Number(project.approved_baseline.value_tco2e).toLocaleString("en-US", { maximumFractionDigits: 1 })} tCO₂e/yr
              </span>
            </div>
          )}

          {hasFlags && (
            <div className="space-y-1">
              {project.governance_flags.map((flag, i) => (
                <div key={i} className="flex items-center gap-2">
                  <AlertTriangle size={9} className="text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-400">{flag}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-600">
            {project.formula_version && <span>Formula: {project.formula_version}</span>}
            {project.validation_timestamp && <span>Validated: {project.validation_timestamp?.slice(0,10)}</span>}
            {project.invalidated_count > 0 && (
              <span className="text-slate-600">{project.invalidated_count} invalidated runs (QA)</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function PlatformIntegrityDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState("all"); // all | ready | issues
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    client.get("/admin/workspace/platform-integrity")
      .then(r => { setData(r.data); setError(null); })
      .catch(e => {
        if (e.response?.status === 401 || e.response?.status === 403) {
          setError("Access restricted. This dashboard requires admin privileges.");
        } else {
          setError("Failed to load integrity data. Ensure you are logged in as an admin.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-950">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
        <p className="text-slate-400 text-sm">Loading integrity report...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-950 p-8">
      <div className="bg-red-950/40 border border-red-800 rounded-2xl p-8 max-w-md text-center">
        <Lock size={32} className="text-red-400 mx-auto mb-4" />
        <p className="text-white font-bold text-lg mb-2">Access Restricted</p>
        <p className="text-red-300 text-sm">{error}</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-xs text-slate-400 hover:text-slate-300">
          ← Go back
        </button>
      </div>
    </div>
  );

  const s = data?.summary || {};
  const projects = data?.projects || [];
  const filtered = filter === "ready" ? projects.filter(p => p.report_ready)
                 : filter === "issues" ? projects.filter(p => !p.report_ready || p.governance_flags?.length > 0)
                 : projects;

  const platformScore = s.platform_integrity_score || 0;
  const scoreColor = platformScore >= 80 ? "text-emerald-400" : platformScore >= 60 ? "text-amber-400" : "text-red-400";

  return (
    <div className="min-h-screen bg-slate-950 p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield size={20} className="text-teal-400" />
            <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Admin Control Tower</p>
          </div>
          <h1 className="text-2xl font-black text-white">Platform Integrity Dashboard</h1>
          <p className="text-xs text-slate-500 mt-1">
            Generated: {data?.generated_at?.slice(0,19).replace("T"," ")} UTC
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Platform score hero */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl px-5 py-3 text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Platform Integrity</p>
            <p className={`text-4xl font-black ${scoreColor}`}>{platformScore}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">/ 100</p>
          </div>
          <button onClick={load}
            className="p-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-colors">
            <RefreshCw size={14} className="text-slate-400" />
          </button>
        </div>
      </div>

      {/* Summary KPI grid — 9 cards */}
      <div className="grid grid-cols-3 lg:grid-cols-9 gap-3 mb-6">
        {[
          { label: "Total Projects",         value: s.total_projects,           icon: BarChart2,    color: "text-white" },
          { label: "Approved Baselines",     value: s.approved_baselines,       icon: Lock,         color: "text-emerald-400" },
          { label: "Draft Runs",             value: s.draft_runs,               icon: Clock,        color: "text-blue-400" },
          { label: "Invalidated Runs",       value: s.invalidated_runs,         icon: XCircle,      color: "text-slate-400" },
          { label: "Validation Failures",    value: s.validation_failures,      icon: AlertCircle,  color: s.validation_failures > 0 ? "text-red-400" : "text-emerald-400" },
          { label: "Report Ready",           value: s.report_ready,             icon: FileText,     color: "text-emerald-400" },
          { label: "Governance Exceptions",  value: s.governance_exceptions,    icon: AlertTriangle,color: s.governance_exceptions > 0 ? "text-amber-400" : "text-emerald-400" },
          { label: "Missing Evidence",       value: s.missing_evidence || 0,    icon: Activity,     color: "text-slate-400" },
          { label: "Missing Baseline",       value: s.missing_approved_baseline,icon: Shield,       color: s.missing_approved_baseline > 0 ? "text-amber-400" : "text-emerald-400" },
        ].map(c => (
          <div key={c.label} className="bg-slate-900 border border-slate-800 rounded-xl p-3 text-center"
            data-testid={`kpi-${c.label.toLowerCase().replace(/\s+/g,"-")}`}>
            <c.icon size={14} className={`${c.color} mx-auto mb-1`} />
            <p className={`text-xl font-black ${c.color}`}>{c.value ?? "—"}</p>
            <p className="text-[9px] text-slate-500 leading-tight mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Score methodology note */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-2.5 mb-5 flex items-center gap-4 flex-wrap">
        <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider flex-shrink-0">Score Methodology:</p>
        {[["Validation","40%","text-teal-400"],["Governance","30%","text-blue-400"],
          ["Traceability","20%","text-purple-400"],["Evidence","10%","text-amber-400"]].map(([l,v,c]) => (
          <span key={l} className="text-[10px] text-slate-400">
            <span className={`font-bold ${c}`}>{v}</span> {l}
          </span>
        ))}
        <span className="text-[10px] text-slate-500 ml-auto">Report Ready threshold: ≥70 + approved baseline + no critical failures</span>
      </div>

      {/* Projects table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        {/* Table header + filter */}
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <p className="text-xs font-bold text-slate-300 uppercase tracking-wider">
            Projects ({filtered.length})
          </p>
          <div className="flex items-center gap-1">
            {[["all","All"],["ready","Report Ready"],["issues","Issues"]].map(([val, lbl]) => (
              <button key={val} onClick={() => setFilter(val)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors
                  ${filter === val ? "bg-teal-600 text-white" : "text-slate-400 hover:text-slate-300"}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* Column headers */}
        <div className="grid px-5 py-2 border-b border-slate-800 bg-slate-800/30"
          style={{ gridTemplateColumns: "48px 1fr 120px 140px" }}>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Score</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider ml-3">Project</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Status</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider text-right">Runs</p>
        </div>

        {/* Project rows */}
        {filtered.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm text-slate-500">No projects match this filter.</p>
          </div>
        ) : (
          filtered.map(p => <ProjectRow key={p.id} project={p} />)
        )}
      </div>
    </div>
  );
}
